import BrowserDatabase from 'Util/BrowserDatabase';
import { showNotification } from 'Store/Notification/Notification.action';
import {
    clearWishlist,
    removeItemFromWishlist as removeItemFromWishlistAction,
    updateAllProductsInWishlist,
    updateIsLoading,
    updateItemOptions
} from 'Store/GuestWishlist/GuestWishlist.action';

import GuestWishlistQuery from './../../query/GuestWishlist.query';
import { fetchMutation } from 'Util/Request/Mutation';
import { isSignedIn } from 'Util/Auth';
// Import the key used by the reducer for local storage
import { GUEST_PRODUCTS_IN_WISHLIST } from 'Store/GuestWishlist/GuestWishlist.reducer';

const CartDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */ 'Store/Cart/Cart.dispatcher'
    );

/**
 * Product GuestWishlist Dispatcher (Local Storage version)
 * @class GuestWishlistDispatcher
 */
export class GuestWishlistDispatcher {
    /**
     * Loads initial wishlist data from local storage.
     */
    updateInitialWishlistData(dispatch) {
        // Simply read from local storage regardless of sign in status
        const products = BrowserDatabase.getItem(GUEST_PRODUCTS_IN_WISHLIST) || {};
        dispatch(updateAllProductsInWishlist(products));
        dispatch(updateIsLoading(false));
    }

    /**
     * Syncs the Redux store with the wishlist saved in local storage.
     */
    _syncWishlistWithLS(dispatch) {
        const products = BrowserDatabase.getItem(GUEST_PRODUCTS_IN_WISHLIST) || {};
        dispatch(updateAllProductsInWishlist(products));
    }

    addItemToWishlist(dispatch, wishlistItem) {
        dispatch(updateIsLoading(true));
        dispatch(showNotification('success', __('Product has been added to your Wish List!')));

        const currentProducts = BrowserDatabase.getItem(GUEST_PRODUCTS_IN_WISHLIST) || {};

        // Use the existing ID if available; otherwise, fall back to the SKU.
        const key = wishlistItem.id || wishlistItem.sku;

        currentProducts[key] = {
            ...wishlistItem,
            wishlist: {
                id: key,
                sku: wishlistItem.sku,
                quantity: wishlistItem.quantity || 1,
                description: wishlistItem.description || ''
            }
        };
        BrowserDatabase.setItem(currentProducts, GUEST_PRODUCTS_IN_WISHLIST);
        this._syncWishlistWithLS(dispatch);
        dispatch(updateIsLoading(false));
        return Promise.resolve();
    }

    /**
     * Updates an item in the wishlist by dispatching new options.
     */
    updateWishlistItem(dispatch, options) {
        // The reducer's updateItemOptions helper will update local storage.
        dispatch(updateItemOptions(options));
        return Promise.resolve();
    }

    /**
     * Clears the entire wishlist.
     */
    clearWishlist(dispatch) {
        // Clear local storage.
        BrowserDatabase.setItem({}, GUEST_PRODUCTS_IN_WISHLIST);
        dispatch(clearWishlist());
        return Promise.resolve();
    }

    /**
     * Moves the wishlist to the cart.
     * (For local storage this simply clears the wishlist and then synchronizes the cart.)
     */
    moveWishlistToCart(dispatch) {
        const guestProducts = BrowserDatabase.getItem(GUEST_PRODUCTS_IN_WISHLIST) || {};
        const wishlistItems = this.mapWishlistDataToCartItems(guestProducts);
        fetchMutation(GuestWishlistQuery.getMoveToCartMutation(wishlistItems)).then(() => {
            CartDispatcher.then(({ default: dispatcher }) =>
                dispatcher._syncCartWithBE(dispatch)
            );
        }).catch((e) => console.error('moveWishlisttocart error'));


        return Promise.resolve();
    }

    /**
     * Removes an item from the wishlist.
     */
    removeItemFromWishlist(dispatch, { item_id, noMessages }) {
        if (!item_id) {
            return null;
        }
        dispatch(updateIsLoading(true));

        if (!noMessages) {
            dispatch(showNotification('info', __('Product has been removed from your Wish List!')));
        }
        // Dispatch the action that will update local storage via the reducer.
        dispatch(removeItemFromWishlistAction(item_id));
        dispatch(updateIsLoading(false));
        return Promise.resolve();
    }

    /**
     * Maps local storage wishlist data to the input format required
     * by the guestWishlistMoveToCart resolver.
     *
     * Expected output for each item:
     * {
     *   sku: "2612",
     *   product_type: "simple",
     *   quantity: 1,
     *   product_option: {
     *     extension_attributes: {}
     *   }
     * }
     *
     * @param {Object} wishlistData - Wishlist data from local storage.
     * @returns {Array} Array of cart items.
     */
    mapWishlistDataToCartItems = (wishlistData) => {
        if (!wishlistData || typeof wishlistData !== 'object') {
            return [];
        }

        return Object.keys(wishlistData).map(key => {
            const product = wishlistData[key];

            const sku = product.wishlist && product.wishlist.sku ? product.wishlist.sku : product.sku;
            const quantity = product.wishlist && product.wishlist.quantity ? product.wishlist.quantity : 1;
            const product_type = product.type_id;
            //const product_option = product.product_option ? product.product_option : { extension_attributes: {} };

            return {
                sku,
                product_type,
                quantity,
                //product_option
            };
        });
    }
}

export default new GuestWishlistDispatcher();
